import { createContext, useContext } from 'react';

type RegistryShoppingContextValue = {
  hasRegistry: boolean;
  registryId: string | null;
  registrySlug: string | null;

  /** If true, we can create the registry for a user if needed.  Otherwise, they should go to onboarding */
  canCreateRegistry: boolean;

  /**
   * Takes the pre-auth or post-auth account (without a registry) to the onboarding
   * flow to either create their account or setup their registry.
   */
  redirectToOnboarding: () => void;

  /** For an existing user, who does not have this type of registry yet, create their registry */
  createRegistryForExistingUser: () => Promise<void>;

  loaded: boolean;
};

export const RegistryShoppingContext = createContext<RegistryShoppingContextValue | undefined>(
  undefined
);

/**
 * Context hook to access the registry shopping context.  Adds support for making
 * decisions about what should happen when an add to registry button is clicked.
 *
 * This is the example pseudo code to follow when clicking add to registry
 *
 * @example
 * const {
 *   hasRegistry,
 *   canCreateRegistry,
 *   redirectToOnboarding,
 *   createRegistryForExistingUser
 * } = useRegistryShoppingContext();
 *
 * if (hasRegistry) {
 *   return addToRegistry();
 * } else (canCreateRegistry) {
 *   return createRegistryForExistingUser().then(addToRegistry())
 * } else {
 *   return redirectToOnboarding();
 * }
 *
 * We pull canCreateRegistry into the provider because the behavior is going to be
 * different for baby and wedding registries.  For baby registries, if you don't have
 * one we want you to go through onboarding.  For example, if you were a zola wedding
 * customer, then you came to the baby site, and clicked add to restistry, we would want
 * you to go through baby onboarding.  So even though you have an a account and we
 * could create the registry, we want you to have the onboarding experience.  For
 * the wedding product, if you've onboarded in one wedding product, we don't want you
 * to onboarded again, so in that case, we can just create the registry for you
 * and add to it.
 */
export const useRegistryShoppingContext = (): RegistryShoppingContextValue => {
  const context = useContext(RegistryShoppingContext);
  if (!context) {
    throw new Error('useRegistryShoppingContext must be used within a Registry Shopping Provider');
  }
  return context;
};
